import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase, clearSupabaseSessionCache } from "@/lib/supabase";

function isAdminRoute(): boolean {
  const path = window.location.pathname;
  const normalized = path.replace(/^\/[^/]+/, "");
  return normalized === "/admin" || normalized.startsWith("/admin/");
}

/**
 * AccountGuard — 前台帳號狀態獨立守衛
 *
 * 此組件只在前台路由渲染，後台 /admin 路由下完全不存在。
 * 職責：
 * 1. 頁面 mount 時立即檢查一次當前登入會員的 account_status
 * 2. 每 1 秒 polling 一次，後台改狀態後前台最遲 1 秒內被強制登出
 * 3. 發現 banned / frozen 時立即 signOut + 強制跳轉
 *    （suspended / 暫停 狀態保持會員在線，不強制登出）
 *
 * 關鍵設計：
 * - 完全獨立於 AuthContext，不影響其他功能
 * - 查詢條件 `eq("id", user.id)` 自然實現「只查目前登入者」的 ID 精確比對
 * - 不使用 Realtime（避免 filter 失效導致全站誤觸發）
 * - broadcastSession: false 已設定，一個 tab 被封不影響其他 tab
 */
export default function AccountGuard() {
  const { user } = useAuth();
  const location = useLocation();
  const isAdmin = location.pathname === "/admin" || location.pathname.startsWith("/admin/");

  useEffect(() => {
    // 後台路由不啟動任何前台狀態監控
    if (isAdmin) return;
    if (!user?.id) return;

    let active = true;

    const checkStatus = async () => {
      if (!active) return;
      if (isAdminRoute()) return;
      if (!user?.id) return;

      try {
        const { data, error } = await supabase
          .from("profiles")
          .select("account_status")
          .eq("id", user.id)
          .maybeSingle();

        if (!active || error || !data) return;

        const st = data.account_status;
        if (st === "banned" || st === "frozen") {
          // 根據狀態導向對應的專屬停用提示頁面
          const redirectPath = st === "frozen" ? "/account-frozen" : "/account-disabled";
          window.location.href = redirectPath;
          clearSupabaseSessionCache();
          // 異步清 session，不阻塞導向
          supabase.auth.signOut().catch(() => {});
          if (st === "banned") {
            // 延遲 alert 避免阻塞頁面跳轉
            setTimeout(() => {
              window.alert("您的帳號已被封鎖，請聯繫客服人員。");
            }, 100);
          }
        }
      } catch (e) {
        console.warn("[AccountGuard] status check error:", e);
      }
    };

    // mount 時立即檢查一次（頁面刷新/重新登入時若已被封鎖，立即踢出）
    checkStatus();

    // 每 1 秒持續輪詢
    const interval = setInterval(checkStatus, 1000);

    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [user?.id, isAdmin]);

  return null;
}